//
// Copyright © 2026 PlayJoy Studios.
//
// Licensed under the Eclipse Public License, Version 2.0 (the "License");
// you may not use this file except in compliance with the License. You may
// obtain a copy of the License at https://www.eclipse.org/legal/epl-2.0
//
// Unless required by applicable law or agreed to in writing, software
// distributed under the License is distributed on an "AS IS" BASIS,
// WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
//
// See the License for the specific language governing permissions and
// limitations under the License.
//

// Timecode text: parse what people type, format for labels. No imports: used by tests in node.

/**
 * Accepts `83`, `1:23`, `1:02:03` and `1h2m3s`.
 * @public
 */
export function parseTimecode (text: string): number | undefined {
  const value = text.trim()
  if (/^\d+(\.\d+)?$/.test(value)) return Number(value)

  const clock = /^(?:(\d+):)?(\d+):(\d{1,2})$/.exec(value)
  if (clock !== null) {
    const hours = clock[1] !== undefined ? Number(clock[1]) : 0
    const minutes = Number(clock[2])
    const seconds = Number(clock[3])
    if (seconds >= 60 || (clock[1] !== undefined && minutes >= 60)) return undefined
    return hours * 3600 + minutes * 60 + seconds
  }

  const units = /^(?:(\d+)h)?\s*(?:(\d+)m)?\s*(?:(\d+)s)?$/i.exec(value)
  if (units !== null && (units[1] ?? units[2] ?? units[3]) !== undefined) {
    return Number(units[1] ?? 0) * 3600 + Number(units[2] ?? 0) * 60 + Number(units[3] ?? 0)
  }

  return undefined
}

/** @public */
export function formatTimecode (seconds: number): string {
  const total = Math.max(0, Math.floor(seconds))
  const hours = Math.floor(total / 3600)
  const minutes = Math.floor((total % 3600) / 60)
  const secs = total % 60
  const pad = (n: number): string => n.toString().padStart(2, '0')
  return hours > 0 ? `${hours}:${pad(minutes)}:${pad(secs)}` : `${minutes}:${pad(secs)}`
}

/** @public */
export function getTimecodeLabel (seconds: number, name: string): string {
  return `▶ ${formatTimecode(seconds)} ${name}`
}
